/**
 * DealOutcomeBadge — "Deal: Won/Lost/Open" badge for the records list row and
 * detail header (Phase C, C-4, WS7 T-15).
 *
 * Fully presentational: the caller passes the outcome straight from the
 * record payload (list/detail endpoints join `call_series_outcomes`
 * server-side, see CallSeriesOutcomeRepository.listBySeriesIds/getBySeries).
 * Null/undefined (not a deal series, or the outcome-sync cron hasn't synced
 * this series yet) hides the badge — no placeholder.
 */

import { useTranslation } from 'react-i18next';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import type { CallSeriesOutcomeSemantic } from '@aica/api-types';

export interface DealOutcomeBadgeProps {
  /** Absent/null => the badge is hidden entirely. */
  outcome?: CallSeriesOutcomeSemantic | null;
  className?: string;
}

const OUTCOME_VARIANT: Record<CallSeriesOutcomeSemantic, BadgeProps['variant']> = {
  won: 'success',
  lost: 'danger',
  open: 'default',
};

export function DealOutcomeBadge({ outcome, className }: DealOutcomeBadgeProps) {
  const { t } = useTranslation('records');

  if (!outcome) return null;

  return (
    <Badge
      variant={OUTCOME_VARIANT[outcome]}
      size="sm"
      data-testid="deal-outcome-badge"
      className={className}
    >
      {t('records.dealOutcome.label', { status: t(`records.dealOutcome.${outcome}`) })}
    </Badge>
  );
}
